import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Shield, Download, Trash2, Eye, FileText, Ban } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ConsentManager from "@/components/ConsentManager";
import { Switch } from "@/components/ui/switch";

// GDPR Article 15: Right of Access (Subject Access Request)
// GDPR Article 17: Right to Erasure ("Right to be Forgotten")

export default function SubjectRights() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sarLoading, setSarLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [erasureLoading, setErasureLoading] = useState(false);
  const [erasureGrounds, setErasureGrounds] = useState("");
  const [ccpaOptOut, setCcpaOptOut] = useState(false);
  const [ccpaLoading, setCcpaLoading] = useState(false);

  // Load CCPA opt-out status on mount
  useEffect(() => {
    const loadCCPAStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data } = await supabase
          .from('ccpa_opt_outs')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        setCcpaOptOut(!!data);
      } catch (error) {
        console.error('Error loading CCPA status:', error);
      }
    };

    loadCCPAStatus();
  }, []);

  // GDPR Article 15: Subject Access Request (1-month response time per ICO)
  const handleSAR = async () => {
    setSarLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("subject_access_requests").insert({
        user_id: user.id,
        data_categories: ["all"], // User requesting all their data
        delivery_method: "email",
      });

      if (error) throw error;

      toast({
        title: "Request Submitted",
        description: "Your Subject Access Request has been submitted. We will respond within 1 month as required by UK GDPR.",
      });
    } catch (error: any) {
      toast({
        title: "Request Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSarLoading(false);
    }
  };

  // Instant SAR Export via edge function
  const handleInstantExport = async () => {
    setExportLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('sar-export', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      // Download the JSON export
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sar-export-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Complete",
        description: "Your personal data has been exported successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  // CCPA Section 1798.120: Do Not Sell My Personal Information
  const handleCCPAOptOut = async (optOut: boolean) => {
    setCcpaLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (optOut) {
        // Opt out of data selling
        const { error } = await supabase.from("ccpa_opt_outs").insert({
          user_id: user.id,
        });
        if (error) throw error;
      } else {
        // Remove opt-out
        const { error } = await supabase
          .from("ccpa_opt_outs")
          .delete()
          .eq("user_id", user.id);
        if (error) throw error;
      }

      setCcpaOptOut(optOut);
      toast({
        title: optOut ? "Opt-Out Enabled" : "Opt-Out Disabled",
        description: optOut 
          ? "You have opted out of data selling as per CCPA rights."
          : "You have re-enabled data sharing with partners.",
      });
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCcpaLoading(false);
    }
  };

  // GDPR Article 17: Right to Erasure Request
  const handleErasureRequest = async () => {
    if (!erasureGrounds.trim()) {
      toast({
        title: "Grounds Required",
        description: "Please provide the legal grounds for your erasure request.",
        variant: "destructive",
      });
      return;
    }

    setErasureLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("erasure_requests").insert({
        user_id: user.id,
        grounds: erasureGrounds,
      });

      if (error) throw error;

      toast({
        title: "Erasure Request Submitted",
        description: "Your request has been submitted for review. We will assess it against UK GDPR requirements and respond within 1 month.",
      });

      setErasureGrounds("");
    } catch (error: any) {
      toast({
        title: "Request Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setErasureLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate("/")}>
            ← Back to Home
          </Button>
        </div>

        <div className="page-header">
          <h1 className="page-title">Your Data Rights</h1>
          <p className="text-lg text-muted-foreground">
            Under UK GDPR and the Data Protection Act 2018, you have the following rights over your personal data.
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="access">Access Data</TabsTrigger>
            <TabsTrigger value="erasure">Delete Data</TabsTrigger>
            <TabsTrigger value="consents">Consents</TabsTrigger>
            <TabsTrigger value="ccpa">CCPA Rights</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Your Rights Under UK GDPR
                </CardTitle>
                <CardDescription>
                  You have comprehensive rights to control your personal data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold">Right of Access</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Request a copy of all personal data we hold about you (Article 15)
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold">Right to Rectification</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Correct any inaccurate or incomplete personal data (Article 16)
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Trash2 className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold">Right to Erasure</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Request deletion of your data ("right to be forgotten") (Article 17)
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Download className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold">Right to Data Portability</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Receive your data in a machine-readable format (Article 20)
                    </p>
                  </div>
                </div>

                <Alert>
                  <AlertDescription>
                    <strong>Response Time:</strong> We will respond to all rights requests within 1 month
                    as required by the UK Information Commissioner's Office (ICO).
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Instant Data Export</CardTitle>
                <CardDescription>
                  Download your personal data immediately (GDPR Article 20 - Data Portability)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Get an instant JSON export of all your personal data including:
                </p>
                <ul className="list-disc ml-6 text-sm space-y-1">
                  <li>Profile information and account details</li>
                  <li>User roles and permissions</li>
                  <li>Consent records with timestamps</li>
                  <li>Criminal record information (if applicable)</li>
                  <li>Lawful basis documentation</li>
                  <li>Audit log history (last 100 events)</li>
                </ul>

                <Button onClick={handleInstantExport} disabled={exportLoading} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  {exportLoading ? "Exporting..." : "Download My Data (Instant)"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Formal Subject Access Request (SAR)</CardTitle>
                <CardDescription>
                  Request a comprehensive report reviewed by our team (GDPR Article 15)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Submit a formal SAR if you need:
                </p>
                <ul className="list-disc ml-6 text-sm space-y-1">
                  <li>Verified and certified copies of your data</li>
                  <li>Information about third-party recipients</li>
                  <li>Detailed processing activity records</li>
                  <li>Data retention justifications</li>
                </ul>

                <Alert>
                  <AlertDescription>
                    <strong>Identity Verification:</strong> For security, we may need to verify your identity
                    before fulfilling this request. We will contact you via your registered email.
                  </AlertDescription>
                </Alert>

                <Button onClick={handleSAR} disabled={sarLoading} className="w-full" variant="outline">
                  {sarLoading ? "Submitting..." : "Submit Formal SAR"}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Response within 1 month. Free of charge (GDPR Article 15).
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="erasure" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Right to Erasure ("Right to be Forgotten")</CardTitle>
                <CardDescription>
                  Request deletion of your personal data under GDPR Article 17
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>
                    <strong>Important:</strong> Erasure may not be possible in all cases. We may be required
                    to retain certain data for legal compliance (e.g., 7-year employment record retention under UK law).
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="erasure-grounds">Legal Grounds for Erasure Request *</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Please select the reason(s) under GDPR Article 17:
                  </p>
                  <Textarea
                    id="erasure-grounds"
                    placeholder="Example: 'The data is no longer necessary for the purpose it was collected' or 'I withdraw my consent for criminal record processing'"
                    value={erasureGrounds}
                    onChange={(e) => setErasureGrounds(e.target.value)}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Valid grounds include: data no longer necessary, consent withdrawn, unlawful processing,
                    objection to legitimate interests, or legal obligation to erase.
                  </p>
                </div>

                <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
                  <p className="font-semibold">What happens after you submit this request?</p>
                  <ol className="list-decimal ml-6 space-y-1">
                    <li>We will review your request against UK GDPR legal requirements</li>
                    <li>If approved, we will securely delete your data and notify third parties (Article 19)</li>
                    <li>If retention is legally required, we will explain the specific legal basis</li>
                    <li>You will receive a response within 1 month</li>
                  </ol>
                </div>

                <Button 
                  onClick={handleErasureRequest} 
                  disabled={erasureLoading}
                  variant="destructive"
                  className="w-full"
                >
                  {erasureLoading ? "Submitting..." : "Request Data Erasure"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="consents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Manage Your Consents</CardTitle>
                <CardDescription>
                  Withdraw or modify consents under GDPR Article 7(3)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConsentManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ccpa" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ban className="h-5 w-5" />
                  California Consumer Privacy Act (CCPA) Rights
                </CardTitle>
                <CardDescription>
                  Rights for California residents under CCPA Section 1798.100-1798.199
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    <strong>CCPA Compliance:</strong> If you are a California resident, you have additional
                    privacy rights under the California Consumer Privacy Act (CCPA).
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Do Not Sell My Personal Information</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Under CCPA Section 1798.120, you have the right to opt-out of the "sale" of your personal
                      information. When enabled, we will not share your data with third parties for monetary
                      or other valuable consideration.
                    </p>
                    
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">Do Not Sell My Data</p>
                        <p className="text-sm text-muted-foreground">
                          {ccpaOptOut ? "Opted out - data sharing disabled" : "Currently allowing data sharing"}
                        </p>
                      </div>
                      <Switch
                        checked={ccpaOptOut}
                        onCheckedChange={handleCCPAOptOut}
                        disabled={ccpaLoading}
                      />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Your CCPA Rights</h3>
                    <ul className="list-disc ml-6 text-sm space-y-2">
                      <li><strong>Right to Know:</strong> Request information about data collected (covered by SAR above)</li>
                      <li><strong>Right to Delete:</strong> Request deletion of personal information (covered by Erasure above)</li>
                      <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (toggle above)</li>
                      <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
                    </ul>
                  </div>

                  <Alert>
                    <AlertDescription className="text-xs">
                      <strong>Response Timeline:</strong> We will respond to CCPA requests within 45 days,
                      with a possible 45-day extension if needed (CCPA Section 1798.130).
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
